import {
  EmergencyLightCheckKind,
  type EmergencyLightResponseDto,
} from "@/generated/model";

/**
 * The two emergency lighting checks (AC11) share one route tree, with the kind as
 * a search param. Keyed on the generated enum so a new server kind fails the build.
 */
export { EmergencyLightCheckKind };

export interface EmergencyLightCheckCopy {
  /** Page and entry-row title. */
  title: string;
  /** One line describing when the check is done. */
  subtitle: string;
  /** Steps shown before the form. */
  instructions: string[];
}

export const EMERGENCY_LIGHT_CHECK_COPY: Record<
  EmergencyLightCheckKind,
  EmergencyLightCheckCopy
> = {
  FLICK_TEST: {
    title: "Emergency Lighting: Monthly Flick Test",
    subtitle: "Isolate the mains and confirm every emergency light illuminates",
    instructions: [
      "Use a fish key to isolate mains power — do not shut off the main lighting distribution board.",
      "Walk all escape routes and confirm every emergency light illuminates.",
      "Restore power and confirm the charging indicators (green LEDs) are active.",
    ],
  },
  VISUAL_CHECK: {
    title: "Emergency Lighting: Visual Check",
    subtitle:
      "Daily or weekly, depending on your policy — confirm each light is undamaged and charging",
    instructions: [
      "Check each emergency light is in place, undamaged and unobstructed.",
      "Confirm the charging indicator (green LED) is lit.",
      "Note anything that needs attention against the light it affects.",
    ],
  },
};

export const EMERGENCY_LIGHT_CHECK_KINDS = Object.keys(
  EMERGENCY_LIGHT_CHECK_COPY,
) as EmergencyLightCheckKind[];

/** Narrows an unvalidated search param to a kind, defaulting to the flick test. */
export function toEmergencyLightCheckKind(
  value: unknown,
): EmergencyLightCheckKind {
  return EMERGENCY_LIGHT_CHECK_KINDS.includes(value as EmergencyLightCheckKind)
    ? (value as EmergencyLightCheckKind)
    : EmergencyLightCheckKind.FLICK_TEST;
}

/** Heading for lights that aren't in a room (NAU-440). */
export const NO_ROOM_LABEL = "No room assigned";

export interface EmergencyLightRoomGroup {
  key: string;
  label: string;
  lights: EmergencyLightResponseDto[];
}

/**
 * Groups the register by room for the check form, keeping the list order.
 * Lights with no room come last, under {@link NO_ROOM_LABEL}.
 */
export function groupLightsByRoom(
  lights: EmergencyLightResponseDto[],
): EmergencyLightRoomGroup[] {
  const groups = new Map<string, EmergencyLightRoomGroup>();
  const noRoom: EmergencyLightResponseDto[] = [];
  for (const light of lights) {
    if (!light.room) {
      noRoom.push(light);
      continue;
    }
    const group = groups.get(light.room.id) ?? {
      key: light.room.id,
      label: light.room.name,
      lights: [],
    };
    group.lights.push(light);
    groups.set(light.room.id, group);
  }

  const roomGroups = Array.from(groups.values());
  if (noRoom.length === 0) return roomGroups;
  return [
    ...roomGroups,
    { key: "no-room", label: NO_ROOM_LABEL, lights: noRoom },
  ];
}
